/**
 * Pliegue de las burbujas del histórico por APLICACIÓN (goal
 * portal-correcciones-7). El servidor ya pliega por `repo` —quedándose con la
 * etiqueta del corte más reciente (goal 6)—, pero hay productos que se cortan
 * desde dos repos (`portal` = server + web, `tienda online` = v2 + v2-pro) y el
 * cliente veía dos burbujas con el mismo rótulo. Aquí se funden las opciones
 * cuyo rótulo resultante es idéntico: la regla se APILA sobre la del goal 6, no
 * la revoca.
 */
import type { ReleaseProductOption } from './releases'

/**
 * Una aplicación de cara al cliente, que puede salir de más de un repo. Cumple
 * `ReleaseProductOption` a propósito: su `repo` es el **CSV** que
 * `GET /releases?repo=` ya entiende como OR desde el goal 6, así que
 * `ReleaseFilterBubbles` y la URL siguen hablando de «un repo» sin enterarse
 * del pliegue.
 */
export interface ReleaseProductGroup extends ReleaseProductOption {
  /** Los repos del grupo, en el mismo orden estable del CSV. */
  repos: string[]
}

/**
 * La clave con la que se decide que dos rótulos son el mismo: `trim()` +
 * minúsculas, con los espacios de sobra colapsados. **No** toca tildes ni
 * adivina sinónimos — `product` es texto libre que el pipeline reescribe en
 * cada corte, y dar por hecho que «Portal» y «El portal» son lo mismo sería
 * inventar.
 */
function claveDeRotulo(product: string): string {
  return product.replace(/\s+/g, ' ').trim().toLowerCase()
}

/**
 * Funde las opciones que comparten rótulo. El orden lo sigue decidiendo el
 * servidor (lo más reciente primero): cada grupo hereda la posición y la
 * etiqueta de su repo mejor colocado, y su `total` es la suma de los de sus
 * repos. El CSV sale en orden **alfabético** —no en el del servidor, que cambia
 * cuando entra un corte nuevo—: es parte de la URL compartible y de la clave
 * con la que se compara la burbuja activa.
 */
export function agruparBurbujasPorProducto(
  options: ReleaseProductOption[],
): ReleaseProductGroup[] {
  const grupos: ReleaseProductGroup[] = []
  const indicePorRotulo = new Map<string, number>()

  for (const option of options) {
    const clave = claveDeRotulo(option.product)
    const indice = indicePorRotulo.get(clave)
    if (indice === undefined) {
      indicePorRotulo.set(clave, grupos.length)
      grupos.push({
        repo: option.repo,
        product: option.product,
        total: option.total,
        repos: [option.repo],
      })
      continue
    }
    const previo = grupos[indice]
    grupos[indice] = {
      ...previo,
      total: previo.total + option.total,
      repos: [...previo.repos, option.repo],
    }
  }

  return grupos.map((grupo) => {
    const repos = [...grupo.repos].sort()
    return { ...grupo, repos, repo: repos.join(',') }
  })
}

/**
 * Qué burbuja marca lo que trae la URL, devuelto como el CSV completo del
 * grupo. Los repos pedidos tienen que ser un **subconjunto** de los de un
 * grupo: así `?repo=tecopos-portal-web` —enlaces ya compartidos y el historial
 * del navegador— marca «portal» y pide sus dos repos. Un repo que no está en
 * ningún grupo (una nota retirada, un slug renombrado) no selecciona nada: se
 * cae a «Todas».
 */
export function grupoSeleccionado(
  grupos: ReleaseProductGroup[],
  repoDeLaUrl: string | null,
): string | null {
  const pedidos = (repoDeLaUrl ?? '')
    .split(',')
    .map((repo) => repo.trim())
    .filter(Boolean)
  if (pedidos.length === 0) return null
  const grupo = grupos.find((g) => pedidos.every((repo) => g.repos.includes(repo)))
  return grupo?.repo ?? null
}
